import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Row } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useEventLogClinicSummary from '../../hooks/useEventLogClinicSummary';
import useEventLogUsers from '../../hooks/useEventLogUsers';
import { EventLogUser } from '../../services/eventLog.service';
import Header from '../layout/Header';
import ExpandedMetricEvents from './ExpandedMetricEvents';
import { metricKeyToEventType } from './metricToEventType';

const formatMetricKey = (key: string): string => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

type MetricRow = {
  metricKey: string;
  metricName: string;
  value: number;
};

const EventLogClinicDetail: React.FC = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const id = Number(clinicId);
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [activeTab, setActiveTab] = useState<'metrics' | 'users'>('metrics');

  const { data: summary, isFetching: summaryLoading } = useEventLogClinicSummary(
    id,
    startDate,
    endDate
  );
  const { data: users, isFetching: usersLoading } = useEventLogUsers(id, startDate, endDate);

  // Convert metrics object to array of rows for the DataTable
  const metricRows = useMemo<MetricRow[]>(() => {
    const metrics = summary?.metrics || {};

    return Object.entries(metrics)
      .map(([key, value]) => ({
        metricKey: key,
        metricName: formatMetricKey(key),
        value: Number(value),
      }))
      .sort((a, b) => b.value - a.value);
  }, [summary]);

  const metricColumns = useMemo<ColumnDef<MetricRow, unknown>[]>(
    () => [
      {
        accessorKey: 'metricName',
        header: 'Metric',
        cell: ({ row }) => <span className="font-medium">{row.original.metricName}</span>,
        enableSorting: true,
      },
      {
        accessorKey: 'value',
        header: 'Count',
        cell: ({ row }) => <span className="font-semibold">{row.original.value}</span>,
        meta: { align: 'right' as const },
        enableSorting: true,
      },
    ],
    []
  );

  const userColumns = useMemo<ColumnDef<EventLogUser, unknown>[]>(
    () => [
      {
        accessorKey: 'userName',
        header: 'User',
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900">{row.original.userName}</p>
            <p className="text-xs text-gray-500">
              {row.original.role || 'No role'} · {row.original.userEmail}
            </p>
          </div>
        ),
        enableSorting: true,
      },
      {
        id: 'session_count',
        accessorFn: row => Number(row.metrics.session_count) || 0,
        header: 'Sessions',
        meta: { align: 'right' as const },
        enableSorting: true,
      },
      {
        id: 'active_time_minutes',
        accessorFn: row => Number(row.metrics.active_time_minutes) || 0,
        header: 'Active Min',
        meta: { align: 'right' as const },
        enableSorting: true,
      },
      {
        id: 'encounters_signed',
        accessorFn: row => Number(row.metrics.encounters_signed) || 0,
        header: 'Encounters Signed',
        meta: { align: 'right' as const },
        enableSorting: true,
      },
      {
        id: 'appointments_created',
        accessorFn: row => Number(row.metrics.appointments_created) || 0,
        header: 'Appointments',
        meta: { align: 'right' as const },
        enableSorting: true,
      },
      {
        id: 'voice_to_chart_uses',
        accessorFn: row => Number(row.metrics.voice_to_chart_uses) || 0,
        header: 'V2C Uses',
        meta: { align: 'right' as const },
        enableSorting: true,
      },
      {
        id: 'patients_created',
        accessorFn: row => Number(row.metrics.patients_created) || 0,
        header: 'Patients Added',
        meta: { align: 'right' as const },
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-3">
            <Link to="/event-logs" className="text-gray-400 hover:text-gray-600">
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <span>{summary?.clinic?.name || `Clinic ${clinicId}`}</span>
          </div>
        }
        rightSide={
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="rounded border-gray-300 shadow-sm text-xs px-2 py-1"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="rounded border-gray-300 shadow-sm text-xs px-2 py-1"
            />
          </div>
        }
      />

      {/* Tabs */}
      <div className="px-6 border-b border-gray-200">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('metrics')}
            className={`py-3 text-sm font-medium border-b-2 ${
              activeTab === 'metrics'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Metrics
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 text-sm font-medium border-b-2 ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Users ({users?.length || 0})
          </button>
        </nav>
      </div>

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <div className="p-6">
          <DataTable
            columns={metricColumns}
            data={metricRows}
            size="default"
            striped
            loading={summaryLoading}
            emptyState={<p className="text-gray-500">No metrics for this period</p>}
            expandable
            renderExpandedRow={(row: Row<MetricRow>) => (
              <ExpandedMetricEvents
                clinicId={id}
                metricKey={metricKeyToEventType(row.original.metricKey)}
                startDate={startDate}
                endDate={endDate}
              />
            )}
            toolbar={{
              search: {
                placeholder: 'Search...',
              },
            }}
          />
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="p-6">
          <DataTable
            columns={userColumns}
            data={users || []}
            loading={usersLoading}
            emptyState={<p className="text-gray-500">No user data for this period</p>}
            size="default"
            striped
            toolbar={{
              search: {
                placeholder: 'Search...',
              },
            }}
          />
        </div>
      )}
    </>
  );
};

export default EventLogClinicDetail;
